import CloseIcon from "../../../assets/versionControl/close.svg?react";

import "./RestoreObjectModal.css";
import { JSX, useContext, useEffect, useState } from "react";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import Modal from "react-modal";
import ChangedField from "../../JobsPanel/EditedFeatures/ChangedField";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import { restoreAttributes, restoreNauticalObjectFully, restoreSectorData } from "../../../utils/versioningUtils";
import { AppContext } from "../../../Context";
import { toast } from "react-toastify";
import RadioButtonGroup from "../../genericComponents/RadioButton/RadioButtonGroup";
import { hasSectors } from "../../../utils/lighthouseUtils";
import Loader from "../../genericComponents/Loader/Loader";
import { useDispatch } from "react-redux";
import { setLoading } from "../../../store/appSlice";
import type Graphic from "@arcgis/core/Graphic";

interface RestoreObjectModalProps {
    isRestoreModalOpen: boolean;
    closeModal: () => void;
    selectedFeature: Graphic;
    restorableObjects: { defaultFeature: Graphic | null; operativeFeature: Graphic | null };
    isLoadingRestorableObjects: boolean;
}

const RestoreObjectModal = (props: RestoreObjectModalProps) => {
    enum RestoreSource {
        Planroot = 0,
        Operative = 1,
    }

    const dispatch = useDispatch();

    const [isRestoring, setIsRestoring] = useState(false);
    const [changedFields, setChangedFields] = useState<JSX.Element[]>([]);
    const [restoreSource, setRestoreSource] = useState<RestoreSource>(RestoreSource.Planroot);
    const [hasSectorData, setHasSectorData] = useState<boolean>(false);

    const appContext = useContext(AppContext);

    const restoreObject = async (fromOperative: boolean, attributesOnly: boolean) => {
        setIsRestoring(true);
        if (attributesOnly) {
            if (fromOperative && props.restorableObjects.operativeFeature) {
                const restoredObject = await restoreAttributes(
                    props.restorableObjects.operativeFeature,
                    props.selectedFeature,
                    true
                );
                if (restoredObject) {
                    appContext?.selectedFeature.set(restoredObject);
                    toast.success("Attributter gjenopprettet fra operativ versjon.");
                }
            }
            if (!fromOperative && props.restorableObjects.defaultFeature) {
                const restoredObject = await restoreAttributes(
                    props.restorableObjects.defaultFeature,
                    props.selectedFeature,
                    true
                );
                if (restoredObject) {
                    appContext?.selectedFeature.set(restoredObject);
                    toast.success("Attributter gjenopprettet fra planroot versjon.");
                }
            }
        } else {
            if (fromOperative && props.restorableObjects.operativeFeature) {
                const restoredObject = await restoreNauticalObjectFully(
                    props.restorableObjects.operativeFeature,
                    props.selectedFeature,
                    true
                );
                if (restoredObject) {
                    appContext?.selectedFeature.set(restoredObject);
                    toast.success("Objekt gjenopprettet fra operativ versjon.");
                }
            }
            if (!fromOperative && props.restorableObjects.defaultFeature) {
                const restoredObject = await restoreNauticalObjectFully(
                    props.restorableObjects.defaultFeature,
                    props.selectedFeature,
                    false
                );
                if (restoredObject) {
                    appContext?.selectedFeature.set(restoredObject);
                    toast.success("Objekt gjenopprettet fra planroot versjon.");
                }
            }
        }
        setIsRestoring(false);
    };

    const restoreSectorDataOnly = async (fromOperative: boolean) => {
        setIsRestoring(true);
        if (fromOperative && props.restorableObjects.operativeFeature) {
            await restoreSectorData(props.selectedFeature, undefined, props.restorableObjects.operativeFeature);
        }
        if (!fromOperative && props.restorableObjects.defaultFeature) {
            await restoreSectorData(props.selectedFeature, props.restorableObjects.defaultFeature);
        }
        setIsRestoring(false);
    };

    const renderChangedFields = () => {
        const changedFieldsDefault: JSX.Element[] = [];
        const changedFieldsOperative: JSX.Element[] = [];
        const layerId = (props.selectedFeature.layer as FeatureLayer).layerId;
        if (layerId === null || layerId === undefined) return setChangedFields([]);
        for (const [field, value] of Object.entries(props.selectedFeature.attributes)) {
            if (
                field === "lighthouseData" ||
                field === "objectid" ||
                field === "globalid" ||
                field === "oppdatert_dato"
            ) {
                continue;
            }
            if (value !== props.restorableObjects.defaultFeature?.attributes[field]) {
                changedFieldsDefault.push(
                    <ChangedField
                        key={field}
                        field={field}
                        fromOperative={false}
                        showAsFromTo
                        editedFeature={{
                            layerId,
                            objectId: props.selectedFeature.attributes.objectid,
                            type: "update",
                            versionGraphic: props.selectedFeature,
                            defaultGraphic: props.restorableObjects.defaultFeature ?? undefined,
                        }}
                    />
                );
            }
            if (value !== props.restorableObjects.operativeFeature?.attributes[field]) {
                changedFieldsOperative.push(
                    <ChangedField
                        key={field}
                        field={field}
                        fromOperative={true}
                        showAsFromTo
                        editedFeature={{
                            layerId,
                            objectId: props.selectedFeature.attributes.objectid,
                            type: "update",
                            versionGraphic: props.selectedFeature,
                            operativeGraphic: props.restorableObjects.operativeFeature ?? undefined,
                        }}
                    />
                );
            }
        }
        const jsxElements: JSX.Element[] = [];
        if (changedFieldsDefault.length > 0) {
            jsxElements.push(
                <Collapsible title="Vis endringer mot planroot" isOpen={false} setIsOpen={() => {}}>
                    {changedFieldsDefault}
                </Collapsible>
            );
        }
        if (changedFieldsOperative.length > 0) {
            jsxElements.push(
                <Collapsible title="Vis endringer mot operativ" isOpen={false} setIsOpen={() => {}}>
                    {changedFieldsOperative}
                </Collapsible>
            );
        }
        setChangedFields(jsxElements);
    };

    useEffect(() => {
        if (!props.isLoadingRestorableObjects && props.selectedFeature && props.isRestoreModalOpen) {
            renderChangedFields();
        }

        if (props.selectedFeature) {
            hasSectors(props.selectedFeature).then((hasSectors) => {
                setHasSectorData(hasSectors);
            });
        } else {
            setHasSectorData(false);
        }
    }, [props.restorableObjects, props.isLoadingRestorableObjects, props.selectedFeature, props.isRestoreModalOpen]);

    useEffect(() => {
        dispatch(setLoading(isRestoring));
    }, [isRestoring]);

    return (
        <Modal
            isOpen={props.isRestoreModalOpen}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            onRequestClose={() => {
                isRestoring ? null : props.closeModal();
            }}
            style={{
                overlay: {
                    background: "rgba(37, 37, 37, 0.50)",
                },
                content: {
                    top: "50%",
                    left: "50%",
                    right: "auto",
                    bottom: "auto",
                    marginRight: "-50%",
                    transform: "translate(-50%, -50%)",
                    padding: 0,
                    background: "none",
                    border: "none",
                },
            }}
        >
            <div className="restore-object-modal-container">
                {isRestoring && <Loader overlay="fullscreen" />}
                <div className="restore-object-modal-header">
                    <h2>Gjenopprettingsmeny</h2>
                    <button className="empty-button" onClick={props.closeModal}>
                        <CloseIcon />
                    </button>
                </div>
                <div className="restore-object-modal-content">
                    <span>
                        Du kan velge mellom å gjenopprette fra planroot eller sist operativ versjon (dersom
                        tilgjengelig).
                    </span>
                    <br />
                    <span>Du kan velge å gjenopprette kun attributter eller fullstendig objekt.</span>
                    <br />
                    <span>For kun attributter vil kun dette spesfikt valgte objektet bli gjenopprettet</span>
                    <br />
                    <span>
                        For fullstendig gjenoppretting vil geometri, attributter, sektordata(vis tilgjengelig) og
                        tilknyttede objekter(IB, Racon, flytendemerke) bli gjenopprettet.
                    </span>
                    <br />

                    <br />
                    <span>Rød tekst er nåværende verdi mens grønn tekst er verdi etter gjenoppretting</span>
                    <div className="changed-fields-container">{changedFields}</div>
                </div>
                <div className="restore-object-modal-footer">
                    <RadioButtonGroup
                        options={["Planroot", "Sist operativ"]}
                        selectedIndex={restoreSource}
                        onChange={(value) => {
                            setRestoreSource(value as RestoreSource);
                        }}
                    />
                    <div className="flex-row small-gap">
                        <NfsButton
                            outlined
                            disabled={
                                props.isLoadingRestorableObjects ||
                                (restoreSource === RestoreSource.Planroot && !props.restorableObjects.defaultFeature) ||
                                (restoreSource === RestoreSource.Operative &&
                                    !props.restorableObjects.operativeFeature) ||
                                isRestoring
                            }
                            onClick={async () => {
                                await restoreObject(restoreSource === RestoreSource.Operative, true);
                            }}
                        >
                            Gjenopprett attributter
                        </NfsButton>
                        {hasSectorData && (
                            <NfsButton
                                outlined
                                disabled={
                                    props.isLoadingRestorableObjects ||
                                    (restoreSource === RestoreSource.Planroot &&
                                        !props.restorableObjects.defaultFeature) ||
                                    (restoreSource === RestoreSource.Operative &&
                                        !props.restorableObjects.operativeFeature) ||
                                    isRestoring
                                }
                                onClick={async () => {
                                    await restoreSectorDataOnly(restoreSource === RestoreSource.Operative);
                                }}
                            >
                                Gjenopprett sektordata
                            </NfsButton>
                        )}
                        <NfsButton
                            outlined
                            disabled={
                                props.isLoadingRestorableObjects ||
                                (restoreSource === RestoreSource.Planroot && !props.restorableObjects.defaultFeature) ||
                                (restoreSource === RestoreSource.Operative &&
                                    !props.restorableObjects.operativeFeature) ||
                                isRestoring
                            }
                            onClick={async () => {
                                await restoreObject(restoreSource === RestoreSource.Operative, false);
                            }}
                        >
                            Gjenopprett fullstendig objekt
                        </NfsButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default RestoreObjectModal;
